import { useQuery } from "@apollo/client";
import { Alert, Button, CircularProgress, Snackbar, Tooltip, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useState } from "react";

import { StyledDataTable } from "@/components/molecules/StyledDataTable";
import {
  GetCallsStatsQuery,
  GetCallsStatsQueryVariables,
  GetPageCallRecordsQuery,
  GetPageCallRecordsQueryVariables,
} from "@/graphql/graphql";
import {
  CallHeadSection,
  CheckTable,
  CurrentWeekOverview,
  Label,
  MetricsData,
  MetricsDetails,
  MetricsHead,
  MetricTableSection,
  RightHeaderDiv,
  RightStyledH1,
  TableHeadTitle,
  TableLabel,
  TableSection,
  TopSection,
  VoicemessageTitle,
  VoicemessageTitleLink,
  WeekDetails,
  WeekOverview,
} from "@/pages/UserDetails/styles";
import { difference } from "@/pages/Users/columns";
import { UserDisplayButton } from "@/styles/flex";
import { Playlist } from "@/types/api";
import { secondsToHMS, transformDate } from "@/utils/date-utils";

import CallPlanCurrentWeekOverview from "./CallPlanCurrentWeekOverview";
import { conditionalRowStyles, USAGE_HISTORY_TABLE_COLUMNS } from "./columns";
import { GET_CALL_RECORDS, GET_CALLS_STATS } from "./queries";
import { UsageHistoryProps } from "./types";

const DEFAULT_PAGE_SIZE = 20;

type CallRecord = GetPageCallRecordsQuery["callHistory"]["items"][number];

export const UsageHistory: React.FC<UsageHistoryProps> = ({ accountId, account }) => {
  const [showError, setShowError] = useState<boolean>(false);
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE);
  const [includeFailed, setIncludeFailed] = useState<boolean>(false);
  const [startOfPage, setStartOfPage] = useState<number>(0);

  const {
    data: calls,
    error,
    loading,
  } = useQuery<GetPageCallRecordsQuery, GetPageCallRecordsQueryVariables>(GET_CALL_RECORDS, {
    variables: {
      accountId,
      includeFailed,
      offset: startOfPage,
      limit: perPage,
    },
  });

  const { data: callsSummary, error: callsSummaryError } = useQuery<
    GetCallsStatsQuery,
    GetCallsStatsQueryVariables
  >(GET_CALLS_STATS, {
    variables: {
      accountId,
    },
  });

  const dataPlaylist: Playlist[] | undefined = account?.playlists;
  const activePlaylist = dataPlaylist?.find((item) => item?.isActive);
  const activeCallPlan = account.callPlans?.find((cp) => cp.isActive);

  const handlePageChange = (page: number) => {
    setStartOfPage(page > 1 ? perPage * (page - 1) : 0);
  };

  const handlePerRowsChange = (newPerPage: number) => {
    setPerPage(newPerPage);
  };

  const CustomLoader = () => (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      <CircularProgress />
      <Typography>Loading entries...</Typography>
    </Box>
  );

  const handleRowClicked = async (row: CallRecord) => {
    const downloadUrl = row?.recordingDownloadUrl?.split("https://api.twilio.com/")[1];
    const newWindow = window.open(`/Call-Record/${row.sid}/${downloadUrl}`);

    newWindow &&
      (newWindow["state"] = {
        state: { accountId: accountId, selectedRecord: row },
      });
  };

  const featurePlaylist = activePlaylist?.playlistVoiceMessages?.filter((message) => {
    return (
      message?.voiceMessage?.feature !== "Instructions" &&
      message?.voiceMessage?.cognitiveDomains?.[0] !== "Transition"
    );
  });

  const diff = difference(account);

  const toggleShowCalls = async () => {
    setIncludeFailed((prev) => !prev);
  };

  return (
    <>
      <RightHeaderDiv>
        <RightStyledH1>Usage History</RightStyledH1>

        <TopSection>
          <TableSection>
            <TableLabel>
              <strong>Current Week Overview</strong>
            </TableLabel>
            <CurrentWeekOverview>
              {activeCallPlan ? (
                <CallPlanCurrentWeekOverview
                  accountTimeZone={account?.timeZone}
                  activeCallPlan={activeCallPlan}
                />
              ) : (
                <>
                  <WeekOverview>
                    <WeekDetails>
                      <Label>Week</Label>
                      {diff ? diff : "-"}
                    </WeekDetails>
                    <WeekDetails>
                      <Label>Start date:</Label>
                      {activePlaylist?.isActive
                        ? transformDate(activePlaylist?.startOn?.toString())
                        : "NR"}
                    </WeekDetails>
                    <WeekDetails>
                      <Label>End date:</Label>
                      {activePlaylist?.isActive
                        ? transformDate(activePlaylist?.endOn?.toString())
                        : "NR"}
                    </WeekDetails>
                  </WeekOverview>
                  <WeekOverview>
                    {featurePlaylist?.map((message, index) => {
                      return (
                        <WeekDetails key={index}>
                          <Label> HBA {index + 1}</Label>
                          <Tooltip
                            title={
                              <Typography fontSize={14}>
                                {message?.voiceMessage?.title ? message?.voiceMessage?.title : "NR"}
                              </Typography>
                            }
                            placement="top"
                            arrow
                          >
                            {!message?.voiceMessage?.templateLink ? (
                              <VoicemessageTitle>
                                {message?.voiceMessage?.title ? message?.voiceMessage?.title : "NR"}
                              </VoicemessageTitle>
                            ) : (
                              <VoicemessageTitleLink
                                onClick={() => window.open(message?.voiceMessage?.templateLink)}
                              >
                                {message?.voiceMessage?.title ? message?.voiceMessage?.title : "NR"}
                              </VoicemessageTitleLink>
                            )}
                          </Tooltip>
                        </WeekDetails>
                      );
                    })}
                  </WeekOverview>
                </>
              )}
            </CurrentWeekOverview>
          </TableSection>
          <TableSection>
            <TableLabel>
              <strong>Call Metrics Summary</strong>
            </TableLabel>
            <MetricTableSection>
              <MetricsHead>
                <TableHeadTitle>Metric</TableHeadTitle>
                <TableHeadTitle>Calls</TableHeadTitle>
                <TableHeadTitle>Call time</TableHeadTitle>
              </MetricsHead>
              <MetricsDetails>
                <MetricsData>Current week</MetricsData>
                <MetricsData>
                  {callsSummary?.callsStatistics?.currentWeek?.count ?? "NR"}
                </MetricsData>
                <MetricsData>
                  {secondsToHMS(callsSummary?.callsStatistics?.currentWeek?.durationSecs) ?? "NR"}
                </MetricsData>
              </MetricsDetails>
              <MetricsDetails>
                <MetricsData>Previous week</MetricsData>
                <MetricsData>
                  {callsSummary?.callsStatistics?.previousWeek?.count ?? "NR"}
                </MetricsData>
                <MetricsData>
                  {secondsToHMS(callsSummary?.callsStatistics?.previousWeek.durationSecs) ?? "NR"}
                </MetricsData>
              </MetricsDetails>
              <MetricsDetails>
                <MetricsData>Previous 4 weeks</MetricsData>
                <MetricsData>
                  {callsSummary?.callsStatistics?.previous4Weeks?.count ?? "NR"}
                </MetricsData>
                <MetricsData>
                  <strong>
                    {secondsToHMS(callsSummary?.callsStatistics?.previous4Weeks?.durationSecs) ??
                      "NR"}
                  </strong>
                </MetricsData>
              </MetricsDetails>
              <MetricsDetails>
                <MetricsData>Current Round</MetricsData>
                <MetricsData>
                  {callsSummary?.callsStatistics?.currentRound?.count ?? "NR"}
                </MetricsData>
                <MetricsData>
                  <strong>
                    {secondsToHMS(callsSummary?.callsStatistics?.currentRound?.durationSecs) ??
                      "NR"}
                  </strong>
                </MetricsData>
              </MetricsDetails>
              <MetricsDetails>
                <MetricsData>All time</MetricsData>
                <MetricsData>{callsSummary?.callsStatistics?.allTime?.count ?? ""}</MetricsData>
                <MetricsData>
                  {!callsSummary?.callsStatistics?.allTime?.count ? (
                    "NR"
                  ) : (
                    <strong>
                      {secondsToHMS(callsSummary?.callsStatistics?.allTime?.durationSecs) ?? "NR"}
                    </strong>
                  )}
                </MetricsData>
              </MetricsDetails>
            </MetricTableSection>
          </TableSection>
        </TopSection>

        <CallHeadSection>
          <div>
            <TableLabel>
              <strong>{!includeFailed ? "Successful Calls" : "All Calls"}</strong>
            </TableLabel>
            <div style={{ marginBottom: "10px" }}>Displayed in Browser Time Zone</div>
          </div>
          <UserDisplayButton>
            <Button onClick={toggleShowCalls} variant="outlined">
              {includeFailed ? "Show Successful Calls" : "Show All Calls"}
            </Button>
          </UserDisplayButton>
        </CallHeadSection>
      </RightHeaderDiv>

      <CheckTable>
        <StyledDataTable
          columns={USAGE_HISTORY_TABLE_COLUMNS}
          data={calls?.callHistory.items ?? []}
          pagination
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={calls?.callHistory.totalCount}
          defaultSortFieldId={1}
          defaultSortAsc={false}
          conditionalRowStyles={conditionalRowStyles}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          progressPending={loading}
          progressComponent={<CustomLoader />}
          sortServer
          onRowClicked={handleRowClicked}
          paginationDefaultPage={1}
          paginationResetDefaultPage={false}
        />
      </CheckTable>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          {error && "Failed to fetch usage history data."}
          {callsSummaryError && "Failed to fetch call summary data."}
        </Alert>
      </Snackbar>
    </>
  );
};
